import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, Search } from 'lucide-react';

type Option = { code: string; label: string };

interface MultiSelectProps {
  options: Option[];
  value: string[]; // selected codes, ['ALL'] means no filter
  onChange: (selected: string[]) => void;
  placeholder?: string;
  maxHeight?: number;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ options, value, onChange, placeholder = 'Select...', maxHeight = 300 }) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const isAll = Array.isArray(value) && value.includes('ALL');
  const selectedValues = isAll ? [] : (value || []);

  // Filter options based on search query
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(opt => opt.label.toLowerCase().includes(query));
  }, [options, searchQuery]);

  // Reset search when closing
  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  const toggleOption = (code: string) => {
    if (isAll) {
      // if ALL was set, clear ALL and start fresh with this option
      onChange([code]);
      return;
    }
    if (selectedValues.includes(code)) {
      const next = selectedValues.filter(c => c !== code);
      if (next.length === 0) onChange(['ALL']); else onChange(next);
    } else {
      onChange([...selectedValues, code]);
    }
  };

  const selectAll = () => onChange(['ALL']);
  const clearAll = () => onChange(['ALL']);

  const label = (() => {
    if (isAll) return placeholder.startsWith('All') ? placeholder : 'All';
    if (!selectedValues || selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) return options.find(o => o.code === selectedValues[0])?.label || selectedValues[0];
    return `${selectedValues.length} selected`;
  })();

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        className="flex items-center gap-2 h-9 px-3 border rounded-md text-sm bg-white min-w-[220px] max-w-[360px]"
        aria-label="Open multi-select"
        type="button"
      >
        <div className="flex items-center justify-between w-full min-w-0">
          {/* Truncate label so the trigger keeps a stable width instead of growing */}
          <span className="truncate w-full block">{label}</span>
          <ChevronDown className="h-4 w-4 opacity-50 ml-2 flex-shrink-0" />
        </div>
      </Popover.Trigger>
      <Popover.Content className="z-50 w-[260px] rounded-md border bg-white p-2 shadow-md" sideOffset={6}>
        {/* Search Input */}
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className="w-full h-8 pl-8 pr-3 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center justify-between px-1 py-1">
          <button className="text-xs text-slate-600 hover:underline" onClick={(e) => { e.stopPropagation(); selectAll(); }}>
            Select All
          </button>
          <button className="text-xs text-slate-600 hover:underline" onClick={(e) => { e.stopPropagation(); clearAll(); }}>
            Clear
          </button>
        </div>
        <div style={{ maxHeight }} className="overflow-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-2 py-2 text-sm text-gray-500 text-center">
              No clients found
            </div>
          ) : (
            filteredOptions.map(opt => {
              const checked = opt.code === 'ALL' ? isAll : (isAll ? false : selectedValues.includes(opt.code));
              return (
                <div key={opt.code} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleOption(opt.code); }}>
                  <div className={`h-4 w-4 flex items-center justify-center rounded border ${checked ? 'bg-tw-primary text-white border-tw-primary' : 'bg-white'}`}>
                    {checked && <Check className="h-3 w-3" />}
                  </div>
                  <div className="text-sm truncate">{opt.label}</div>
                </div>
              );
            })
          )}
        </div>
        <Popover.Arrow className="fill-white" />
      </Popover.Content>
    </Popover.Root>
  );
};

export default MultiSelect;
